import { FieldStructureMap } from '../../../service-request-form-response.mapper.util';
import {
  buildChecklistField,
  buildTechnicianSignatureFieldStructure,
  SHARED_GENERAL_INFO_LEFT_KEYS,
} from '../shared/static-form-fields.util';
import { GroupedChecklistInsertDefinition } from '../shared/static-form-section.util';
import {
  PP_DISP_FINAL_EVALUATION_CHECKLIST_KEYS,
  PP_DISP_FINAL_EVALUATION_LEFT_FIELD_ENTRIES,
  PP_DISP_FINAL_EVALUATION_RIGHT_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_CHECKLIST_KEYS,
  PP_DISP_FOLLOWUP_CORRECTION_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_DATE_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_LEADING_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_PHOTO_SLOTS,
  PP_DISP_FOLLOWUP_TECH_SIGNATURE_FOOTER_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_TECH_SIGNATURE_LAYOUT,
  PP_DISP_INSPECTION_LEFT_FIELD_ENTRIES,
  PP_DISP_INSPECTION_OUTCOME_OPTIONS,
  PP_DISP_INSPECTION_RIGHT_FIELD_ENTRIES,
  PP_DISP_SIGNOFF_FOOTER_FIELD_ENTRIES,
  PP_DISP_SIGNOFF_LAYOUT,
} from '../pp-disp/pp-disp-form.fields';

const field = (
  controlKey: string,
  label: string,
  sequence: number,
  overrides: Partial<FieldStructureMap[string]> = {},
): [string, FieldStructureMap[string]] => [
  controlKey,
  {
    type: overrides.type ?? 'TEXT',
    label,
    sequence,
    required: overrides.required ?? false,
    ...overrides,
  },
];

export { SHARED_GENERAL_INFO_LEFT_KEYS as CI_GENERAL_INFO_LEFT_KEYS };

export const CI_GENERAL_INFO_RIGHT_KEYS = [
  'customerAccount',
  'shipToLocation',
  'shipToCoordinates',
  'shipToLastInspection',
] as const;

const buildCiGeneralInfoFields = (): [string, FieldStructureMap[string]][] => [
  field('serviceRequestNo', $localize`:@@serviceRequestNo:Service Request No.`, 2, { readOnly: true }),
  field('serviceRequestType', $localize`:@@serviceRequestType:Service Request Type`, 3, { readOnly: true }),
  field('dateCreated', $localize`:@@dateCreated:Date Created`, 4, { readOnly: true }),
  field('createdBy', $localize`:@@createdBy:Created By`, 5, { readOnly: true }),
  field('customerAccount', $localize`:@@ciPlantName:Plant Name`, 6, { readOnly: true }),
  field('shipToLocation', $localize`:@@ciLocation:Location`, 7, { readOnly: true }),
  field('shipToCoordinates', $localize`:@@ciLongitudeLatitude:Longitude Latitude`, 8, { readOnly: true }),
  field('shipToLastInspection', $localize`:@@ciShipToLastInspection:Ship-To Last Inspection`, 9, {
    readOnly: true,
  }),
];

export const CI_FACILITY_VESSEL_LOCATION_FIELD_ENTRIES: [string, { required?: boolean }][] = [
  ['vesselLocation', { required: true }],
];

export const CI_FACILITY_VESSEL_LOCATION_PHOTO_SLOTS = [
  { controlKey: 'facilityVesselLocationPhoto', label: 'Photo' },
] as const;

export const CI_FACILITY_TEXT_FIELD_ENTRIES: [string, { required?: boolean; maxLength?: number }][] = [
  ['locationCustomerOrFacility', { required: true, maxLength: 100 }],
  ['facilityService', { required: true, maxLength: 100 }],
];

export const CI_FACILITY_SERIAL_FIELD_ENTRIES: [string, { required?: boolean }][] = [
  ['serialNo', { required: true }],
];

export const CI_FACILITY_SERIAL_PHOTO_SLOTS = [
  { controlKey: 'facilitySerialNoPhoto', label: 'Photo' },
] as const;

export const CI_FACILITY_YEAR_BUILT_FIELD_ENTRIES: [string, { required?: boolean; maxLength?: number }][] = [
  ['yearBuilt', { required: true, maxLength: 100 }],
];

export const CI_FACILITY_CAPACITY_FIELD_ENTRIES: [string, { required?: boolean }][] = [
  ['capacity', { required: true }],
];

export const CI_FACILITY_CAPACITY_PHOTO_SLOTS = [
  { controlKey: 'facilityCapacityPhoto', label: 'Photo' },
] as const;

export const CI_FACILITY_MANUFACTURED_BY_FIELD_ENTRIES: [string, { required?: boolean; maxLength?: number }][] = [
  ['manufacturedBy', { required: true, maxLength: 100 }],
];

export const CI_FACILITY_CRN_FIELD_ENTRIES: [string, { required?: boolean }][] = [['crnNo', { required: true }]];

export const CI_FACILITY_CRN_PHOTO_SLOTS = [{ controlKey: 'facilityCrnPhoto', label: 'Photo' }] as const;

export const CI_FACILITY_PROV_NUMBER_FIELD_ENTRIES: [string, { required?: boolean }][] = [
  ['provNumberAbsaTsbc', { required: true }],
];

export const CI_FACILITY_PROV_NUMBER_PHOTO_SLOTS = [
  { controlKey: 'facilityProvNumberPhoto', label: 'Photo' },
] as const;

const CI_OBSERVATION_A_LABELS: Record<string, string> = {
  observationSectionA01: $localize`:@@ciObsA01:Condition of tanks(s) is adequate - with no peeling paint, damage or corrosion`,
  observationSectionA02: $localize`:@@ciObsA02:Storage transfer hoses & fittings in good condition - with no excessive chaffing or wear to metal braiding - are annual inspections current - identifier`,
  observationSectionA03: $localize`:@@ciObsA03:Hoses neatly hung up, away from vehicle traffic & not laying on ground`,
  observationSectionA04: $localize`:@@ciObsA04:Pressure relief valves & hoses protected from elements & off ground - venting stacks and shall be metallic material and vented vertically upward.`,
  observationSectionA05: $localize`:@@ciObsA05:Electrical connections adequately protected - in good repair`,
  observationSectionA06: $localize`:@@ciObsA06:Storagte tank is grounded`,
  observationSectionA07: $localize`:@@ciObsA07:Pumps function properly and in good repair - guards in place`,
  observationSectionA08: $localize`:@@ciObsA08:Float gauge functions properly`,
  observationSectionA09: $localize`:@@ciObsA09:Rotary gauge functions properly`,
  observationSectionA10: $localize`:@@ciObsA10:Storage tank is labeled and placard properly (UN1075)`,
  observationSectionA11: $localize`:@@ciObsA11:Vehicle protection in place - by posts, jersey barriers or fencing in good shape`,
  observationSectionA12: $localize`:@@ciObsA12:Valves and controls function properly - in good working order`,
  observationSectionA13: $localize`:@@ciObsA13:Markings adequate to identify liquid or vapor - inlets and outlets marked`,
  observationSectionA14: $localize`:@@ciObsA14:“Lock outs” for mechanical or electrical (if present) are controlled`,
  observationSectionA15: $localize`:@@ciObsA15:Set backs tank located a minimum of 50’ from building structures & overhead power lines (tanks >= 42,000 gallons up to authority having jurisdiction)`,
  observationSectionA16: $localize`:@@ciObsA16:Emergency shut offs function properly (air or mechanical). If air cylinders are used for air shut down, are the cylinders properly labeled for contents?`,
  observationSectionA17: $localize`:@@ciObsA17:Piping has flow directions`,
  observationSectionA18: $localize`:@@ciObsA18:Piping and supports - piping is properly supported/braced`,
  observationSectionA19: $localize`:@@ciObsA19:Piping conditions meet standards - no leaks or corrosion`,
  observationSectionA20: $localize`:@@ciObsA20:Piping paint in good condition.`,
  observationSectionA21: $localize`:@@ciObsA21:Safe loading procedure sign posted at transfer area & tank storage area`,
  observationSectionA22: $localize`:@@ciObsA22:Ground cable(s)- in good condition & properly affixed in tank storage area`,
  observationSectionA23: $localize`:@@ciObsA23:Steps or loading docks have guardrails?`,
  observationSectionA24: $localize`:@@ciObsA24:Emergency shut offs- are ESV's readily accessible & visible`,
};

export const CI_OBSERVATION_SECTION_A_KEYS = Object.keys(CI_OBSERVATION_A_LABELS) as (keyof typeof CI_OBSERVATION_A_LABELS)[];

export const CI_OBSERVATION_SECTION_A_FOOTER = {
  notesControlKey: 'observationSectionAComments',
  notesLabel: $localize`:@@ciObsAComments:Comments`,
} as const;

const CI_OBSERVATION_PHOTO_SLOT_COUNT = 3;

export const CI_OBSERVATION_A_INSERT_AFTER: GroupedChecklistInsertDefinition[] = [
  { afterItemNumber: 1, photoControlKey: 'observationAAfter01Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 4, photoControlKey: 'observationAAfter04Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 6, photoControlKey: 'observationAAfter06Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 8, photoControlKey: 'observationAAfter08Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 10, photoControlKey: 'observationAAfter10Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 12, photoControlKey: 'observationAAfter12Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 13, photoControlKey: 'observationAAfter13Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 16, photoControlKey: 'observationAAfter16Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 18, photoControlKey: 'observationAAfter18Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 20, photoControlKey: 'observationAAfter20Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 24, photoControlKey: 'observationAAfter24Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
];

const CI_OBSERVATION_B_LABELS: Record<string, string> = {
  observationSectionB01: $localize`:@@ciObsB01:Evacuation procedures and routes known to personnel - posted`,
  observationSectionB02: $localize`:@@ciObsB02:Evacuation routes are obstruction-free`,
  observationSectionB03: $localize`:@@ciObsB03:Emergency shutoff tested and functioning`,
  observationSectionB04: $localize`:@@ciObsB04:Emergency phone # list posted and current`,
  observationSectionB05: $localize`:@@ciObsB05:Muster station known or posted`,
  observationSectionB06: $localize`:@@ciObsB06:Fire extinguisher free from obstruction`,
  observationSectionB07: $localize`:@@ciObsB07:Fire extinguisher secured and stored properly`,
  observationSectionB08: $localize`:@@ciObsB08:Fire exits marked and functional`,
  observationSectionB09: $localize`:@@ciObsB09:No smoking and no sources of ignition signs posted`,
};

export const CI_OBSERVATION_SECTION_B_KEYS = Object.keys(CI_OBSERVATION_B_LABELS) as (keyof typeof CI_OBSERVATION_B_LABELS)[];

export const CI_OBSERVATION_SECTION_B_FOOTER = {
  notesControlKey: 'observationSectionBComments',
  notesLabel: $localize`:@@ciObsBComments:Comments`,
} as const;

export const CI_OBSERVATION_B_INSERT_AFTER: GroupedChecklistInsertDefinition[] = [
  { afterItemNumber: 1, photoControlKey: 'observationBAfter01Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 2, photoControlKey: 'observationBAfter02Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
  { afterItemNumber: 5, photoControlKey: 'observationBAfter05Photo', maxPhotoCount: CI_OBSERVATION_PHOTO_SLOT_COUNT },
];

export const CI_ASSET_COORDINATOR_FIELD_ENTRIES: [string, { type?: 'date' }][] = [
  ['nextPrvChangeDue', { type: 'date' }],
  ['nextThoroughInspectionDue', { type: 'date' }],
  ['nextFiveYearThoroughVeDue', { type: 'date' }],
];

export const CI_ASSET_COORDINATOR_COMMENTS_KEY = 'assetCoordinatorComments';

export const CI_MANAGEMENT_REVIEW_LEFT_ENTRIES: [string, { type?: 'select' }][] = [
  ['managementReviewInspector', { type: 'select' }],
];

export const CI_MANAGEMENT_REVIEW_RIGHT_ENTRIES: [string, { type?: 'textarea'; maxLength?: number }][] = [
  ['managementReviewInspectorDesignation', { type: 'textarea', maxLength: 100 }],
  ['managementReviewInspectorCertificate', { type: 'textarea', maxLength: 100 }],
  ['managementReviewComments', { type: 'textarea', maxLength: 100 }],
];

export {
  PP_DISP_INSPECTION_LEFT_FIELD_ENTRIES as CI_INSPECTION_LEFT_FIELD_ENTRIES,
  PP_DISP_INSPECTION_RIGHT_FIELD_ENTRIES as CI_INSPECTION_RIGHT_FIELD_ENTRIES,
  PP_DISP_INSPECTION_OUTCOME_OPTIONS as CI_INSPECTION_OUTCOME_OPTIONS,
  PP_DISP_FINAL_EVALUATION_LEFT_FIELD_ENTRIES as CI_FINAL_EVALUATION_LEFT_FIELD_ENTRIES,
  PP_DISP_FINAL_EVALUATION_RIGHT_FIELD_ENTRIES as CI_FINAL_EVALUATION_RIGHT_FIELD_ENTRIES,
  PP_DISP_FINAL_EVALUATION_CHECKLIST_KEYS as CI_FINAL_EVALUATION_CHECKLIST_KEYS,
  PP_DISP_FOLLOWUP_LEADING_FIELD_ENTRIES as CI_FOLLOWUP_LEADING_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_DATE_FIELD_ENTRIES as CI_FOLLOWUP_DATE_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_CORRECTION_FIELD_ENTRIES as CI_FOLLOWUP_CORRECTION_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_CHECKLIST_KEYS as CI_FOLLOWUP_CHECKLIST_KEYS,
  PP_DISP_FOLLOWUP_PHOTO_SLOTS as CI_FOLLOWUP_PHOTO_SLOTS,
  PP_DISP_SIGNOFF_LAYOUT as CI_SIGNOFF_LAYOUT,
  PP_DISP_SIGNOFF_FOOTER_FIELD_ENTRIES as CI_SIGNOFF_FOOTER_FIELD_ENTRIES,
  PP_DISP_FOLLOWUP_TECH_SIGNATURE_LAYOUT as CI_FOLLOWUP_TECH_SIGNATURE_LAYOUT,
  PP_DISP_FOLLOWUP_TECH_SIGNATURE_FOOTER_FIELD_ENTRIES as CI_FOLLOWUP_TECH_SIGNATURE_FOOTER_FIELD_ENTRIES,
};

export const CI_FINAL_EVALUATION_PHOTO_SLOTS = [
  { controlKey: 'installationPhotos', label: $localize`:@@ciInstallationPhotos:Photos (5 Images)`, maxPhotoCount: 5 },
] as const;

export const CI_FINAL_EVALUATION_CHECKLIST_ITEM_1_KEYS = ['installationAcceptable'] as const;
export const CI_FINAL_EVALUATION_CHECKLIST_ITEM_2_KEYS = ['evaluationFollowUpNeeded'] as const;

const observationAFields = (): [string, FieldStructureMap[string]][] => [
  ...CI_OBSERVATION_SECTION_A_KEYS.map((controlKey, index) =>
    buildChecklistField(controlKey, CI_OBSERVATION_A_LABELS[controlKey], 120 + index),
  ),
  field('observationSectionAComments', CI_OBSERVATION_SECTION_A_FOOTER.notesLabel, 145, {
    type: 'TEXT_AREA',
    maxLength: 100,
    isChecklistFooter: true,
  }),
  ...CI_OBSERVATION_A_INSERT_AFTER.map((insert, index) =>
    field(insert.photoControlKey!, $localize`:@@ciObsAPhoto:Photo`, 146 + index, {
      type: 'IMAGE',
      maxPhotoCount: insert.maxPhotoCount ?? CI_OBSERVATION_PHOTO_SLOT_COUNT,
    }),
  ),
];

const observationBFields = (): [string, FieldStructureMap[string]][] => [
  ...CI_OBSERVATION_SECTION_B_KEYS.map((controlKey, index) =>
    buildChecklistField(controlKey, CI_OBSERVATION_B_LABELS[controlKey], 170 + index),
  ),
  field('observationSectionBComments', CI_OBSERVATION_SECTION_B_FOOTER.notesLabel, 182, {
    type: 'TEXT_AREA',
    maxLength: 100,
    isChecklistFooter: true,
  }),
  ...CI_OBSERVATION_B_INSERT_AFTER.map((insert, index) =>
    field(insert.photoControlKey!, $localize`:@@ciObsBPhoto:Photo`, 183 + index, {
      type: 'IMAGE',
      maxPhotoCount: insert.maxPhotoCount ?? CI_OBSERVATION_PHOTO_SLOT_COUNT,
    }),
  ),
];

const facilityFields = (): [string, FieldStructureMap[string]][] => [
  field('vesselLocation', $localize`:@@ciVesselLocation:Vessel Location`, 30, { required: true }),
  field('facilityVesselLocationPhoto', $localize`:@@ciVesselLocationPhoto:Photo`, 31, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('locationCustomerOrFacility', $localize`:@@ciLocationCustomerOrFacility:Location (Customer or Facility)`, 32, {
    required: true,
    maxLength: 100,
  }),
  field('facilityService', $localize`:@@ciFacilityService:Service`, 33, { required: true, maxLength: 100 }),
  field('serialNo', $localize`:@@ciSerialNo:Serial No.`, 34, { required: true }),
  field('facilitySerialNoPhoto', $localize`:@@ciSerialNoPhoto:Photo`, 35, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('yearBuilt', $localize`:@@ciYearBuilt:Year Built`, 36, { required: true, maxLength: 100 }),
  field('capacity', $localize`:@@ciCapacity:Capacity`, 37, { required: true }),
  field('facilityCapacityPhoto', $localize`:@@ciCapacityPhoto:Photo`, 38, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('manufacturedBy', $localize`:@@ciManufacturedBy:Manufactured By`, 39, { required: true, maxLength: 100 }),
  field('crnNo', $localize`:@@ciCrnNo:CRN No.`, 40, { required: true }),
  field('facilityCrnPhoto', $localize`:@@ciCrnPhoto:Photo`, 41, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('provNumberAbsaTsbc', $localize`:@@ciProvNumber:Prov. Number (ABSA, TSBC)`, 42, { required: true }),
  field('facilityProvNumberPhoto', $localize`:@@ciProvNumberPhoto:Photo`, 43, { type: 'IMAGE', maxPhotoCount: 1 }),
];

const assetAndManagementFields = (): [string, FieldStructureMap[string]][] => [
  field('nextPrvChangeDue', $localize`:@@ciNextPrvChangeDue:Next PRV Change Due`, 200, { type: 'DATE' }),
  field('nextThoroughInspectionDue', $localize`:@@ciNextThoroughInspectionDue:Next Thorough Inspection Due`, 201, {
    type: 'DATE',
  }),
  field('nextFiveYearThoroughVeDue', $localize`:@@ciNextFiveYearThoroughVeDue:Next 5-Year Thorough VE Due`, 202, {
    type: 'DATE',
  }),
  field('assetCoordinatorComments', $localize`:@@ciAssetCoordinatorComments:Comments`, 203, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
  field('managementReviewInspector', $localize`:@@ciManagementReviewInspector:Inspector`, 210, {
    type: 'USER_LOOK_UP',
    required: true,
  }),
  field('managementReviewInspectorDesignation', $localize`:@@ciManagementReviewDesignation:Inspector Designation`, 211, {
    type: 'TEXT_AREA',
    maxLength: 100,
  }),
  field('managementReviewInspectorCertificate', $localize`:@@ciManagementReviewCertificate:Inspector Certificate Number`, 212, {
    type: 'TEXT_AREA',
    maxLength: 100,
  }),
  field('managementReviewComments', $localize`:@@ciManagementReviewComments:Comments`, 213, {
    type: 'TEXT_AREA',
    maxLength: 100,
  }),
];

const finalEvaluationAndFollowUpFields = (): [string, FieldStructureMap[string]][] => [
  field('evaluationInspector', $localize`:@@ciEvaluationInspector:Inspector`, 220, { type: 'USER_LOOK_UP', required: true }),
  field('inspectorCertificateNumber', $localize`:@@ciInspectorCertificateNumber:Inspector Certificate Number`, 221, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  buildChecklistField('installationAcceptable', $localize`:@@ciInstallationAcceptable:Is the installation acceptable?`, 222),
  field('installationPhotos', CI_FINAL_EVALUATION_PHOTO_SLOTS[0].label, 223, { type: 'IMAGE', maxPhotoCount: 5 }),
  buildChecklistField('evaluationFollowUpNeeded', $localize`:@@ciEvaluationFollowUpNeeded:Is an inspection follow up needed?`, 225),
  field('deficiency', $localize`:@@ciDeficiency:Deficiency`, 230, { type: 'TEXT_AREA', maxLength: 100 }),
  field('followupInspectionDate', $localize`:@@ciFollowupInspectionDate:Followup Inspection Date`, 231, { type: 'DATE' }),
  field('complianceTimeframe', $localize`:@@ciComplianceTimeframe:Compliance Timeframe (Days)`, 232),
  buildChecklistField('makingCorrections', $localize`:@@ciMakingCorrections:Are you making corrections?`, 233),
  field('followupCorrectiveAction', $localize`:@@ciFollowupCorrectiveAction:Followup/Corrective Action`, 234, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
  field('deficiencyCorrectedBy', $localize`:@@ciDeficiencyCorrectedBy:Deficiency Corrected By`, 235),
  field('deficiencyCorrectedDate', $localize`:@@ciDeficiencyCorrectedDate:Deficiency Corrected Date`, 236, { type: 'DATE' }),
  field('pictureOfRedTag', $localize`:@@ciPictureOfRedTag:Picture of Red Tag`, 237, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('correctionPhoto', $localize`:@@ciCorrectionPhoto:Photo of Correction`, 238, { type: 'IMAGE', maxPhotoCount: 1 }),
  field('correctionComments', $localize`:@@ciCorrectionComments:Comments of correction`, 239, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
  field('followupTechnicianCertificateNumber', $localize`:@@ciFollowupTechnicianCertificate:Technician Certificate Number`, 240),
  field('signOffComments', $localize`:@@ciSignOffComments:Comments`, 241, { type: 'TEXT_AREA', maxLength: 150 }),
  field('actionTaken', $localize`:@@ciActionTaken:Action Taken`, 242, { type: 'TEXT_AREA', maxLength: 150 }),
  field('technicianComments', $localize`:@@ciTechnicianComments:Technician Comments/Remarks`, 243, {
    type: 'TEXT_AREA',
    maxLength: 150,
  }),
];

export const CI_FIELD_STRUCTURE: FieldStructureMap = Object.fromEntries([
  ...buildCiGeneralInfoFields(),
  field('inspectionNo', $localize`:@@ciInspectionNo:Inspection No.`, 20, { readOnly: true }),
  field('inspectionType', $localize`:@@ciInspectionType:Inspection Type`, 21, { readOnly: true }),
  field('inspectionDate', $localize`:@@ciInspectionDate:Inspection Date`, 22, { type: 'DATE' }),
  field('inspectionOutcome', $localize`:@@ciInspectionOutcome:Inspection Outcome`, 23, {
    type: 'SINGLE',
    required: true,
    subQuestionLabels: ['Pass', 'Fail'],
  }),
  field('outcomeRemarks', $localize`:@@ciOutcomeRemarks:Outcome Remarks`, 24, {
    type: 'TEXT_AREA',
    required: true,
    maxLength: 100,
  }),
  ...facilityFields(),
  ...observationAFields(),
  ...observationBFields(),
  ...finalEvaluationAndFollowUpFields(),
  ...assetAndManagementFields(),
  ...Object.entries(buildTechnicianSignatureFieldStructure(250)),
]);

/** Control keys assigned question IDs 99001+ (excludes shared general info and technician signature). */
export const CI_QUESTION_ID_CONTROL_KEYS: string[] = [
  ...facilityFields().map(([key]) => key),
  ...observationAFields().map(([key]) => key),
  ...observationBFields().map(([key]) => key),
  ...finalEvaluationAndFollowUpFields().map(([key]) => key),
  ...assetAndManagementFields().map(([key]) => key),
  'inspectionNo',
  'inspectionType',
  'inspectionDate',
  'inspectionOutcome',
  'outcomeRemarks',
];
